/**
 * PDF unter festen Adressen (S54): a business keeps a few slots („Mittagskarte“, „Abendkarte“), each
 * with an address that never changes, `…/mittagskarte.pdf`. The house links it once from its own
 * website; publishing a menu as PDF into the slot replaces what is there. Until the first PDF the
 * address gives a small one saying the menu follows.
 */

export interface PdfSlot {
  id: string;
  /** What the house calls it; may change. */
  name: string;
  /** The last part of its address, from the name when it was made; never changes. */
  slug: string;
  /** The PDF in the business's store (files.ts on the server), its hash and „.pdf“; null until the first. */
  file: string | null;
  bytes: number;
  pages: number;
  publishedAt: number | null;
  /** Who published it, by name, and which menu with its title then. */
  publishedBy: string;
  menuId: string | null;
  menuTitle: string;
  createdAt: number;
  /** The house's own address by a redirect on its website (S40), checked; „“ none. */
  redirectUrl: string;
  /** What the PDF showed when it was made (S62, Z-0: `pdfDigestOf` in the app); „“ unknown, made before. */
  digest: string;
  /** In which language it was made (`cardLangKey`: „de“, „en“, „en+de“); „“ unknown. */
  lang: string;
  /** Where it can be had (not stored): at Cloudflare or at this server, and under the house's own domain (S40). */
  url?: string;
  ownUrl?: string;
}

export const PDF_SLOT_LIMITS = {
  perBusiness: 5,
  name: 40,
  /** A PDF at most this large; Caddy lets 20 MB through for this path (S54). */
  bytes: 20_000_000,
  pages: 40,
} as const;

/** The last part of an address from a name: „Mittagskarte (Sommer)“ → „mittagskarte-sommer“. */
export function slugOf(name: string): string {
  const s = name
    .normalize("NFC")
    .toLocaleLowerCase("de-DE")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    // Room for „-2“ and so on.
    .slice(0, 36)
    .replace(/-+$/g, "");
  return s || "karte";
}

/** A slug not yet taken: „mittagskarte“, else „mittagskarte-2“ … */
export function freeSlug(name: string, taken: ReadonlySet<string>): string {
  const base = slugOf(name);
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
}

/** A PDF's mark and language as the app sends them (S62, Z-0). */
export const PDF_DIGEST = /^[0-9a-f]{8}$/;
export const PDF_LANG = /^(?:de|[a-z]{2}(?:\+de)?)$/;

/** What a slug may look like in an address. */
export const SLUG = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
